import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Card, MetricsHeader, ErrorState, MoneyValue, Skeleton } from '@shared/design-system/ui'
import type { Currency } from '@shared/contracts/enums'
import type { DateRangeValue } from '@shared/lib/date-range'
import type { analyticsPeriod } from '@shared/lib/analytics-period'
import type { MonetaryValue } from '@shared/lib/money/money'
import { salesAnalyticsQuery } from '../api/sales-analytics'
import {
  adjustmentsNote,
  contentCard,
  kpiCard,
  kpiGrid,
  kpiHint,
  kpiLabel,
  kpiValue,
} from './sales.css'

export function SalesAnalyticsPanel({
  period,
  dateRange,
  onDateRangeChange,
  currency,
  onCurrencyChange,
}: {
  period: ReturnType<typeof analyticsPeriod>
  dateRange: DateRangeValue
  onDateRangeChange: (value: DateRangeValue) => void
  currency: Currency
  onCurrencyChange: (currency: Currency) => void
}) {
  const metrics = useQuery({
    ...salesAnalyticsQuery(period.query, !period.error),
    placeholderData: keepPreviousData,
  })
  const data = metrics.data?.byCurrency[currency]
  return (
    <Card padding="md" role="region" aria-label="Resultados das vendas" className={contentCard}>
      <MetricsHeader
        title="Resultado no período"
        titleId="sales-metrics-title"
        dateRange={dateRange}
        onDateRangeChange={onDateRangeChange}
        periodLabel={period.label}
        currency={currency}
        currencies={metrics.data?.currencies ?? []}
        onCurrencyChange={onCurrencyChange}
        currencyAriaLabel="Moeda das vendas"
      />
      {period.error ? (
        <p role="alert">{period.error}</p>
      ) : metrics.isPending || metrics.isPlaceholderData ? (
        <Skeleton height={420} />
      ) : metrics.isError ? (
        <ErrorState onRetry={() => void metrics.refetch()} />
      ) : !data ? (
        <p>Sem movimentações nesta moeda no período.</p>
      ) : (
        <>
          <div className={kpiGrid}>
            <Metric
              label="Bruto no período"
              value={data.total.gross}
              hint={`${data.total.charges} ${data.total.charges === 1 ? 'cobrança paga' : 'cobranças pagas'}`}
            />
            <Metric
              label="Líquido com reserva"
              value={data.total.net}
              hint="Sua parte após taxas e divisões, antes dos ajustes abaixo"
            />
            <Metric
              label="Resultado após ajustes"
              value={data.result}
              hint="Líquido menos perdas e multas, mais devoluções"
            />
          </div>
          <h3>De onde veio o líquido</h3>
          <div className={kpiGrid}>
            {(
              [
                ['Avulsas pagas', data.sources.oneTime],
                ['Primeiras cobranças de assinaturas', data.sources.subscriptionInitial],
                ['Renovações pagas', data.sources.renewals],
              ] as const
            ).map(([label, source]) => (
              <Metric
                key={label}
                label={label}
                value={source.net}
                hint={
                  <>
                    {source.charges} {source.charges === 1 ? 'cobrança' : 'cobranças'} · bruto{' '}
                    <MoneyValue {...source.gross} size="sm" />
                  </>
                }
              />
            ))}
          </div>
          <p className={kpiHint}>
            Uma compra pode conter avulsas e assinaturas. Os valores se somam; a mesma cobrança pode
            aparecer em mais de uma categoria. Reservas fazem parte do líquido, mas podem ainda
            estar indisponíveis para saque.
          </p>
          {data.estimatedAllocation && (
            <p className={kpiHint}>
              Em compras antigas sem divisão registrada por item, o líquido de cada categoria é
              estimado proporcionalmente ao bruto.
            </p>
          )}
          <div className={kpiGrid}>
            <Metric
              label="Novos clientes"
              text={String(data.newCustomers)}
              hint="Primeira compra paga deste produtor, considerando todo o histórico"
            />
            <Metric
              label="Novas assinaturas pagas"
              text={String(data.newSubscriptions)}
              hint="Contratos que tiveram o primeiro pagamento no período"
            />
            <Metric
              label="Multas pagas"
              value={data.adjustments.feesPaid.amount}
              hint={`${data.adjustments.feesPaid.count} ${data.adjustments.feesPaid.count === 1 ? 'multa com desconto efetivo' : 'multas com desconto efetivo'} no período`}
            />
          </div>
          <details>
            <summary>Ajustes financeiros do período</summary>
            <div className={kpiGrid}>
              {data.adjustments.fxLosses ? (
                <Metric
                  label="Perdas cambiais"
                  value={data.adjustments.fxLosses.amount}
                  hint="Sua parte na perda efetiva de conversão"
                />
              ) : null}
              <Metric
                label="Reembolsos"
                value={data.adjustments.refunds.amount}
                hint="Valor descontado da sua participação"
              />
              <Metric
                label="Reembolsos preventivos"
                value={data.adjustments.preventedRefunds.amount}
                hint="Devoluções para prevenir uma contestação"
              />
              <Metric
                label="Chargebacks"
                value={data.adjustments.chargebacks.amount}
                hint="Perdas lançadas por contestações"
              />
              <Metric
                label="Valores devolvidos"
                value={data.adjustments.credits.amount}
                hint="Reversões de perdas e multas"
              />
              <Metric
                label="Multas ainda pendentes"
                value={data.adjustments.feesPending.amount}
                hint="Saldo em aberto hoje; fora do resultado do período"
              />
            </div>
            <p className={`${kpiHint} ${adjustmentsNote}`}>
              Ajustes seguem a data do lançamento ou desconto e podem corresponder a vendas antigas.
              Resultado não é saldo disponível nem lucro contábil.
            </p>
          </details>
          <h3>Reembolsos e chargebacks</h3>
          <div className={kpiGrid}>
            <Metric
              label="Taxa de reembolso"
              text={percentage(data.quality.refunds.pct)}
              hint={`${data.quality.refunds.count} de ${data.quality.refunds.base} cobranças pagas · inclui parcial e preventivo`}
            />
            <Metric
              label="Taxa de chargeback"
              text={percentage(data.quality.chargebacks.pct)}
              hint={`${data.quality.chargebacks.count} de ${data.quality.chargebacks.base} cobranças elegíveis · exclui prevenção`}
            />
          </div>
          <p className={kpiHint}>
            Taxas acompanham as vendas pagas no período e os eventos conhecidos até hoje. Cada
            cobrança conta uma vez por taxa. Chargebacks incluem contestações revertidas; a taxa é
            operacional, não a taxa oficial da bandeira. Valores na moeda registrada, sem nova
            conversão.
          </p>
        </>
      )}
    </Card>
  )
}

function percentage(value: number | null) {
  return value === null
    ? 'Sem base'
    : `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`
}
function Metric({
  label,
  value,
  text,
  hint,
}: {
  label: string
  value?: MonetaryValue
  text?: string
  hint: React.ReactNode
}) {
  return (
    <div className={kpiCard}>
      <span className={kpiLabel}>{label}</span>
      {value ? <MoneyValue {...value} size="lg" /> : <span className={kpiValue}>{text}</span>}
      <span className={kpiHint}>{hint}</span>
    </div>
  )
}
